import { useEffect, useState } from 'react';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Form,
  FormInstance,
  GetProp,
  message,
  Upload,
  UploadFile,
  UploadProps,
  Image,
} from 'antd';
import { Rule } from 'antd/lib/form';
import { RcFile } from 'antd/es/upload';

import styles from './index.module.scss';
import { useFileUpload } from '../../../hooks/api/files';
import { FileData } from '../../../store/types';

const beforeUpload = (
  file: Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]
) => {
  const isImage = file.type.startsWith('image/');
  const isLt5M = file.size / 1024 / 1024 < 5;

  if (!isImage) {
    message.error('Only image files are allowed!');
    return false;
  }

  if (!isLt5M) {
    message.error('Image must be smaller than 5MB!');
  }

  return isImage && isLt5M;
};

interface FileProps {
  form?: FormInstance;
  defaultFiles?: { id: string; location: string; originalName: string }[];
  label?: string;
  name?: string;
  rules?: Rule[];
  uploadBtn?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onFileDataChange?: (fileData: FileData[]) => void;
  uploadKey?: number;
}

export default function InputFiles({
  form,
  defaultFiles = [],
  name,
  rules = [{ required: true, message: 'Please upload files' }],
  label,
  uploadBtn,
  className,
  disabled,
  onFileDataChange,
  uploadKey = 0,
}: FileProps) {
  const fileUpload = useFileUpload();
  const [fileData, setFileData] = useState<FileData[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (defaultFiles.length) {
      const initialFiles: UploadFile[] = defaultFiles.map(
        ({ id, location, originalName }) => ({
          uid: id,
          url: location,
          name: originalName,
          status: 'done',
        })
      );
      setFileList(initialFiles);
      form?.setFieldValue(
        name,
        initialFiles.map((file) => file.uid)
      );
    }
  }, [defaultFiles, form, name]);

  useEffect(() => {
    onFileDataChange?.(fileData);
  }, [fileData]);

  useEffect(() => {
    const data = fileUpload?.data;
    if (!data) return;

    setFileData((prev) => [...prev, data]);

    const uploadedFile: UploadFile = {
      uid: data.id,
      name: data.originalName,
      status: 'done',
      url: data.location,
    };

    const updatedList = [...fileList, uploadedFile];
    setFileList(updatedList);
    form?.setFieldValue(
      name,
      updatedList.map((f) => f.uid)
    );
  }, [fileUpload?.data]);

  const handleRemove = (file: UploadFile) => {
    const updatedList = fileList.filter((f) => f.uid !== file.uid);
    setFileList(updatedList);
    form?.setFieldValue(
      name,
      updatedList.map((f) => f.uid)
    );
    setFileData((prev) => prev.filter((f) => f.id !== file.uid));
  };

  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || '');
    setPreviewOpen(true);
  };

  const uploadButtonRender = uploadBtn ?? (
    <button className={styles.fileBox} type="button">
      {fileUpload.loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return (
    <Form.Item name={name} rules={rules} className={styles.file} label={label}>
      <Upload
        key={uploadKey}
        disabled={disabled}
        accept="image/*"
        listType="picture-card"
        multiple
        fileList={fileList}
        showUploadList
        beforeUpload={beforeUpload}
        maxCount={8}
        onRemove={handleRemove}
        onPreview={handlePreview}
        customRequest={({ file, onSuccess, onError }) => {
          try {
            fileUpload.fetchFile(file as RcFile);
            onSuccess?.('ok'); // simulate success for UI
          } catch (err) {
            console.error('❌ Upload error:', err);
            onError?.(err as Error);
          }
        }}
        className={className}
      >
        {fileList.length >= 8 ? null : uploadButtonRender}
      </Upload>

      {previewImage && (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      )}
    </Form.Item>
  );
}
